import React, { createContext, useReducer, useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LoadingOverlay,
  Box,
  Text,
  Stack,
  ThemeIcon,
  Center,
  Group,
  Loader,
  Button,
  MantineProvider,
  Alert,
} from "@mantine/core";
import {
  IconShieldCheck,
  IconFaceIdError,
  IconExclamationCircle,
} from "@tabler/icons-react";
import ErrorMessage from "../components/error-message";
import Navigation from "../components/navigation";
import CenterLoadingOverlay from "../components/center-loading-overlay";
import EnglishLoadingWithCompletion from "../components/english-loading-with-completion";
import {
  useVisitorData,
  FingerprintJSPro,
} from "@fingerprintjs/fingerprintjs-pro-react";
import { ensureTrailingSlash } from "../utils/url";
import { getEdgeSessionUrl } from "../utils/edge";
import {
  browserName,
  browserVersion,
  fullBrowserVersion,
  osName,
  osVersion,
  deviceType,
  mobileVendor,
  mobileModel,
  getUA
} from "react-device-detect";
// Creating the context
export const DataContext = createContext();

// Initial state for the reducer
const initialState = {
  data: null,
  settings: null,
  text: null,
  routeSequence: [],
  sessionState: {},
  currentIndex: 0,
  alreadyDone: false,
  triggerNavigation: false,
  status: "PENDING",
  totalRoutes: 0,
  completedRoutes: 0,
  selectedComponent: null,
  modularFailedAtLeastOne: false,
  referrer: "",
  closedClicked: false,
  confirmationNumber: null, // Add this to track the latest confirmation number
  cameraInfo: null, // Store camera info from Microblink
  // Add other states as necessary
};

// Reducer function to handle state changes
const dataReducer = (state, action) => {
  switch (action.type) {
    case "SET_DATA":
      return { ...state, data: action.payload };
    case "SET_STATUS":
      return { ...state, status: action.payload };
    case "SET_SELECTED_COMPONENT":
      return { ...state, selectedComponent: action.payload };
    case "SET_CLOSED_CLICKED":
      return { ...state, closedClicked: action.payload };
    case "SET_MODULAR_FAILED_AT_LEAST_ONE":
      return { ...state, modularFailedAtLeastOne: action.payload };
    case "TRIGGER_NAVIGATION":
      return { ...state, triggerNavigation: !state.triggerNavigation }; // Toggle the trigger
    case "SET_IS_CURRENT_COMPONENT_COMPLETE":
      return { ...state, isCurrentComponentComplete: action.payload };
    case "SET_SETTINGS":
      return { ...state, settings: action.payload };
    case "SET_TEXT":
      return { ...state, text: action.payload };
    case "SET_SESSION_STATE":
      return { ...state, sessionState: action.payload };
    case "SET_CURRENT_INDEX":
      return { ...state, currentIndex: action.payload };
    case "SET_ALREADY_DONE":
      return { ...state, alreadyDone: action.payload };
    case "SET_REFERRER":
      return { ...state, referrer: action.payload };
    case "SET_CONFIRMATION_NUMBER":
      return { ...state, confirmationNumber: action.payload };
    case "SET_CAMERA_INFO":
      return { ...state, cameraInfo: action.payload };
    case "SET_ROUTE_SEQUENCE":
      return {
        ...state,
        routeSequence: action.payload,
        totalRoutes: action.payload.length, // Set the total number of routes
      };
    case "INCREMENT_COMPLETED_ROUTES":
      return {
        ...state,
        completedRoutes: state.completedRoutes + 1,
      };
    // Add more cases as needed
    default:
      return state;
  }
};

async function getRequestIdFromSession(session_id) {
  const response = await fetch(
    process.env.REACT_APP_BACKEND_URL + '/v3/stable/api/frontend/exchangeSessionForToken',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ session_id }) // Payload with the session_id
    }
  )

  if (!response.ok) {
    throw new Error(`Error exchanging session: ${response.statusText}`)
  }

  // Sample response: { "request_id": "1f9dfa59-54fa-4067-9a29-26265342b2f2" }
  const result = await response.json()
  return result.request_id
}

// DataProvider component
export const DataProvider = ({ children, request_id, fingerprintDisabled = false, initialSessionData = null }) => {
  // Create a unique instance ID to track if component is remounting
  const instanceId = React.useRef(Math.random().toString(36).substr(2, 9));

  // Always start with default state to prevent remounting
  const [state, dispatch] = useReducer(dataReducer, initialState);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const qr = searchParams.get("qr");
  const nextPath = searchParams.get("nextPath");
  const edge = searchParams.get("edge");
  const language = searchParams.get("language");
  const colorScheme = searchParams.get("colorScheme");
  
  // Check if English loading should be shown
  // Use URL parameter if available, otherwise fall back to browser language
  const browserLanguage = typeof navigator !== "undefined" ? navigator.language : "";
  const effectiveLanguage = language || browserLanguage;
    // Check if URL contains specific words that should skip English loading
  const urlParams = new URLSearchParams(window.location.search);
  const currentUrl = window.location.href.toLowerCase();
  const skipEnglishLoadingWords = ['handoff', 'oneid', 'clear'];
  const shouldSkipEnglishLoading = skipEnglishLoadingWords.some(word => 
    currentUrl.includes(word) || urlParams.has(word)
  );
  
  const shouldShowEnglishLoading = !shouldSkipEnglishLoading && (!effectiveLanguage || effectiveLanguage.toLowerCase() === "" || effectiveLanguage.toLowerCase().startsWith("en"));
  
  // Only require animation completion for English users
  const [loadingAnimationComplete, setLoadingAnimationComplete] = useState(!shouldShowEnglishLoading);
  // Add delay before showing English loading to prevent restart
  const [showEnglishLoadingDelayed, setShowEnglishLoadingDelayed] = useState(false);
  const [fpjs, setFpjs] = useState(fingerprintDisabled ? { visitorId: 'disabled-via-url-param' } : null);
  const [fingerprintSent, setFingerprintSent] = useState(fingerprintDisabled);
  const [errorGettingData, setErrorGettingData] = useState(false);
  const [edgeRetryCount, setEdgeRetryCount] = useState(0);
  const [showHighTrafficMessage, setShowHighTrafficMessage] = useState(false);
  const [overload, setOverload] = useState(false);
  const [isBot, setIsABot] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [testModeActive, setTestModeActive] = useState(false);
  const [isTestModeBannerDismissed, setIsTestModeBannerDismissed] = useState(false);
  const [retryCount, setRetryCount] = useState(0); // Track number of retries
  const edgeRetryStartRef = useRef(null);
  const [highTrafficTimeoutExceeded, setHighTrafficTimeoutExceeded] = useState(false);
  const [tooManyAttempts, setTooManyAttempts] = useState(false);

  // Store the fingerprint disabled state from props
  const [internalFingerprintDisabled, setInternalFingerprintDisabled] = useState(fingerprintDisabled);

  const postMessage = (message) => {
    if (state?.settings?.isReactNative && window.ReactNativeWebView?.postMessage) {
      const data =
        typeof message === "string" ? message : JSON.stringify(message);
      window.ReactNativeWebView.postMessage(data);
    } else {
      window.parent.postMessage(message, "*");
    }
  };

  // Centralized function to send payload messages with consistent structure
  // Only sends for veratad site or sites containing "kid"
  const postPayloadMessage = ({ event, app, error = null, status = null }) => {
    // Only send payload for veratad site or sites containing "kid"
    const siteName = state?.settings?.site_name || "";
    const shouldSendPayload = siteName === "veratad" || siteName.toLowerCase().includes("kid");
    if (!shouldSendPayload) {
      return;
    }

    // If app is not provided or is in path format (idmatch/coppa), try to get the uppercase version from route sequence
    let appName = app;
    if (!app || app.includes('/')) {
      // Try to find the current route's app name
      const currentRoute = state.routeSequence?.[state.currentIndex];
      if (currentRoute?.app) {
        appName = currentRoute.app;
      } else if (app && app.includes('/')) {
        // Convert path format to uppercase format as fallback
        appName = app.replace('/', '_').toUpperCase();
      }
    }

    const payload = {
      messageType: "payload",
      value: {
        event,
        app: appName,
        error,
        status,
        request_id: state.settings?.request_id || request_id,
      },
    };
    postMessage(payload);
  };

  const { getData } = useVisitorData(
    { extendedResult: false, tag: { request_id: request_id } },
    { immediate: false }
  );

  const checkFpjsData = () => {
    // Check if fingerprinting is disabled from props or if we already have dummy data
    if (internalFingerprintDisabled || fpjs?.visitorId === 'disabled-via-url-param') {
      // Make sure we have the dummy data set
      if (!fpjs) {
        setFpjs({ visitorId: 'disabled-via-url-param' });
      }
      
      // Make sure we marked fingerprint as sent
      if (!fingerprintSent) {
        setFingerprintSent(true);
      }
      
      return;
    }
    
    // Check if settings are available and fingerprinting is disabled in settings
    if (state.settings && state.settings.is_fingerprint_active === false) {
      setFpjs({ visitorId: 'disabled-via-settings' });
      setFingerprintSent(true);
      setInternalFingerprintDisabled(true);
      return;
    }
    
    // Check URL parameters as a backup
    const urlParams = new URLSearchParams(window.location.search);
    const fingerprintParam = urlParams.get('fingerprint');
    
    // If fingerprinting disabled via URL param, set dummy data immediately
    if (fingerprintParam === 'f') {
      setFpjs({ visitorId: 'disabled-via-url-param' });
      setFingerprintSent(true);
      setInternalFingerprintDisabled(true);
      return;
    }

    getData()
      .then((data) => {
        setFpjs(data);
        setOverload(false); // Reset overload if successful
      })
      .catch((error) => {
        if (error.message === FingerprintJSPro.ERROR_RATE_LIMIT) {
          setOverload(true);
        } else {
          setErrorGettingData(true);
        }
      });
  };

  useEffect(() => {
    if (overload) {
      const intervalId = setInterval(() => {
        checkFpjsData();
      }, 5000); // Retry every 3 seconds

      return () => clearInterval(intervalId); // Cleanup on unmount
    }
  }, [overload]);

  // Initial fingerprint check and whenever settings change
  useEffect(() => {
    // Only run if fingerprinting is not already disabled
    if (!internalFingerprintDisabled && !fingerprintSent) {
      checkFpjsData();
    }
  }, [state.settings]); // Run when settings change

  // Fallback: if fingerprinting never completes, avoid blocking the flow
  useEffect(() => {
    if (internalFingerprintDisabled || fingerprintSent) return;

    const timeout = setTimeout(() => {
      console.warn('Fingerprinting timeout - proceeding without fingerprint data');
      setFingerprintSent(true);
      setFpjs({ visitorId: 'timeout-fallback' });
    }, 5000);

    return () => clearTimeout(timeout);
  }, [internalFingerprintDisabled, fingerprintSent]);

  useEffect(() => {
    // Check if fingerprinting is disabled or if we've already sent data
    if (internalFingerprintDisabled || fingerprintSent) {
      return;
    }
    
    // Check if we have the settings and fingerprint is active
    if (request_id && fpjs && state.settings) {
      // Skip if fingerprinting is disabled in settings
      if (state.settings && state.settings.is_fingerprint_active === false) {
        setFingerprintSent(true);
        return;
      }
      
      // Skip sending fingerprint data if it's our dummy data
      if (fpjs.visitorId === 'disabled-via-url-param') {
        setFingerprintSent(true);
        return;
      }
      
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json", // Ensures the server knows you're sending JSON
        },
        body: JSON.stringify({
          request_id: request_id,
          fpjs_data: fpjs,
          site_name: state.settings?.site_name || null,
        }),
      };

      fetch("https://vx.dcams.app/frontend/session/fingerprint", requestOptions)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          setFingerprintSent(true);
          setIsABot(data.isBot);
          setTestModeActive(data.testModeActive === true);

          // Handle isBlocked if it exists and is true
          if (data.isBlocked === true) {
            setIsBlocked(true);
            // Send a message to parent window (only if not in test mode)
            if (data.testModeActive !== true) {
              postMessage("ALREADY VERIFIED");
            }
          }
        })
        .catch((error) => {
          //setErrorGettingData(true);
          console.error("Error storing fingerprint:", error);
        });
    }
  }, [request_id, fpjs, fingerprintSent, state.settings, internalFingerprintDisabled]);

  // Fetch initial data with edge retry logic
  const fetchAndCheck = async (url, request_id, isEdge = false) => {
    try {
      if (isEdge && !request_id) {
        console.warn("Edge session request attempted without a request_id");
      }

      // Build payload with device_info when edge mode is active
      const payload = { request_id };

      if (isEdge) {
        payload.device_info = {
          browserName,
          browserVersion,
          fullBrowserVersion,
          osName,
          osVersion,
          deviceType,
          mobileVendor,
          mobileModel,
          userAgent: getUA
        };

        // Include current URL parameters in edge mode
        const currentUrlParams = new URLSearchParams(window.location.search);
        const urlParamsObject = {};
        for (const [key, value] of currentUrlParams.entries()) {
          urlParamsObject[key] = value;
        }
        if (Object.keys(urlParamsObject).length > 0) {
          payload.url_params = urlParamsObject;
        }
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 429) {
        return { tooManyRequests: true };
      }

      if (response.status === 404) {
        return { notFound: true };
      }

      if (!response.ok) {
        console.warn(`Error with request to ${url}: ${response.statusText}`);
        return { otherError: true, status: response.status };
      }

      const result = await response.json();
      
      // Check for edge "Not Found" error specifically
      if (isEdge && result.error === "Not Found" &&
          result.message === "No session data found for the given request ID.") {
        return { edgeNotFound: true };
      }

      if ((!result.data || result.data === false) && result.already_done !== true) {
        console.warn(`No valid data returned from ${url}`);
        return null;
      }

      return result;
    } catch (error) {
      console.warn(`Failed to fetch from ${url}:`, error);
      return { otherError: true };
    }
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleRouteSequence = (dispatch, result, nextPath) => {
    if (result.data.settings && result.data.settings.journey.route) {
      let updatedRouteSequence = result.data.settings.journey.route.map(
        (route) => ({
          ...route,
          shouldProceed: (sessionState) =>
            sessionState[route.shouldProceedKey] === route.expectedStatus,
        })
      );

      if (nextPath) {
        const nextPathIndex = updatedRouteSequence.findIndex(
          (route) => route.path === nextPath
        );

        if (nextPathIndex > -1) {
          updatedRouteSequence = updatedRouteSequence.filter(
            (_, index) => index >= nextPathIndex
          );
        } else {
          const lastRoute =
            updatedRouteSequence[updatedRouteSequence.length - 1];
          const newRoute = {
            ...lastRoute,
            path: nextPath,
            shouldProceedKey: nextPath,
          };

          updatedRouteSequence.unshift(newRoute);
        }

        if (nextPath === "dcams/mitek") {
          updatedRouteSequence = [updatedRouteSequence[0]];
        }
      }

      dispatch({ type: "SET_ROUTE_SEQUENCE", payload: updatedRouteSequence });
    }
  };

  // Check for frontend query param
  const frontendParam = searchParams.get('frontend');
  
  // Determine if we should use VX backend based on hostname or query param
  const useVxBackend = window.location.hostname === "vx-fe.dcams.app" || frontendParam === "vx-fe";
  
  // Check if edge parameter is true OR if request_id ends with "-edge" (for OneID return flow)
  // Also check for stored edge_request_id (for Trinsic edge mode return)
  const stateParam = searchParams.get('state');
  const isEdgeFromRequestId = request_id?.endsWith('-edge') || stateParam?.endsWith('-edge');
  const storedEdgeRequestId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('edge_request_id') : null;
  const isTrinsicEdgeReturn = (
    request_id === 'trinsic/return' ||
    request_id === 'bankid-cz/return' ||
    request_id === 'finnish-trust-network/return' ||
    request_id === 'mojeid/return'
  ) && storedEdgeRequestId;
  const useEdgeEndpoint = edge === "true" || isEdgeFromRequestId || isTrinsicEdgeReturn;
  
  const edgeSessionUrl = getEdgeSessionUrl();
  const vxSessionUrl = ensureTrailingSlash(
    "https://vx.dcams.app/frontend/session/get_session"
  );

  const getPrimaryUrl = useEdgeEndpoint
    ? edgeSessionUrl
    : useVxBackend
    ? vxSessionUrl
    : `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/getUrl`;

  const getSecondaryUrl = useEdgeEndpoint
    ? edgeSessionUrl
    : !useVxBackend
    ? vxSessionUrl
    : `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/getUrl`;

  const handleFatalSessionError = () => {
    setShowHighTrafficMessage(false);
    setHighTrafficTimeoutExceeded(false);
    setEdgeRetryCount(0);
    setLoadingAnimationComplete(true);
    setShowEnglishLoadingDelayed(true);
    setErrorGettingData(true);
    edgeRetryStartRef.current = null;
  };

  const fetchData = async (request_id, dispatch, nextPath, edgeAttempt = 0) => {
    try {
      if (useEdgeEndpoint) {
        if (edgeRetryStartRef.current === null) {
          edgeRetryStartRef.current = Date.now();
          setHighTrafficTimeoutExceeded(false);
        }

        const result = await fetchAndCheck(
          getPrimaryUrl,
          request_id,
          true,
        );

        if (!result) {
          handleFatalSessionError();
          return;
        }

        if (result?.tooManyRequests) {
          setTooManyAttempts(true);
          setShowHighTrafficMessage(false);
          setHighTrafficTimeoutExceeded(false);
          setEdgeRetryCount(0);
          setLoadingAnimationComplete(true);
          edgeRetryStartRef.current = null;
          return;
        }

        if (result?.already_done) {
          setShowHighTrafficMessage(false);
          setHighTrafficTimeoutExceeded(false);
          setEdgeRetryCount(0);
          setLoadingAnimationComplete(true);
          setShowEnglishLoadingDelayed(true);
          dispatch({ type: "SET_ALREADY_DONE", payload: true });
          setErrorGettingData(false);
          setRetryCount(0);
          edgeRetryStartRef.current = null;
          return;
        }

        if (result?.otherError) {
          handleFatalSessionError();
          return;
        }

        if (result.edgeNotFound || result.notFound) {
          const nextAttempt = edgeAttempt + 1;
          setEdgeRetryCount(nextAttempt);

          const elapsed = edgeRetryStartRef.current
            ? Date.now() - edgeRetryStartRef.current
            : 0;
          if (nextAttempt >= 12) {
            setShowHighTrafficMessage(true);
          }

          if (elapsed >= 180000) {
            setHighTrafficTimeoutExceeded(true);
            setShowHighTrafficMessage(false);
            setEdgeRetryCount(0);
            setLoadingAnimationComplete(true);
            edgeRetryStartRef.current = null;
            return;
          }

          await wait(2000);
          return fetchData(request_id, dispatch, nextPath, nextAttempt);
        }

        const wasHighTraffic = showHighTrafficMessage;
        if (showHighTrafficMessage) {
          setShowHighTrafficMessage(false);
        }
        if (edgeRetryCount !== 0) {
          setEdgeRetryCount(0);
        }
        if (highTrafficTimeoutExceeded) {
          setHighTrafficTimeoutExceeded(false);
        }
        if (tooManyAttempts) {
          setTooManyAttempts(false);
        }
        edgeRetryStartRef.current = null;

        if (
          shouldShowEnglishLoading &&
          (wasHighTraffic || !showEnglishLoadingDelayed)
        ) {
          setLoadingAnimationComplete(true);
        }

        const isReactNativeFlag =
          result.data?.isReactNative ??
          result.data?.settings?.isReactNative ??
          false;
        dispatch({
          type: "SET_DATA",
          payload: { ...result.data, isReactNative: isReactNativeFlag },
        });
        dispatch({
          type: "SET_ALREADY_DONE",
          payload: nextPath ? false : result.already_done,
        });

        dispatch({ type: "SET_SETTINGS", payload: result.data.settings });
        dispatch({ type: "SET_TEXT", payload: result.data.settings.text });

        handleRouteSequence(dispatch, result, nextPath);
        setErrorGettingData(false);
        setRetryCount(0);
        return;
      }

      let result = await fetchAndCheck(getPrimaryUrl, request_id, false);

      if (!result) {
        handleFatalSessionError();
        return;
      }

      if (result?.tooManyRequests) {
        setTooManyAttempts(true);
        setShowHighTrafficMessage(false);
        setHighTrafficTimeoutExceeded(false);
        setEdgeRetryCount(0);
        setLoadingAnimationComplete(true);
        edgeRetryStartRef.current = null;
        return;
      }

      if (result?.already_done) {
        setShowHighTrafficMessage(false);
        setHighTrafficTimeoutExceeded(false);
        setEdgeRetryCount(0);
        setLoadingAnimationComplete(true);
        setShowEnglishLoadingDelayed(true);
        dispatch({ type: "SET_ALREADY_DONE", payload: true });
        setErrorGettingData(false);
        setRetryCount(0);
        edgeRetryStartRef.current = null;
        return;
      }

      if (result?.otherError) {
        handleFatalSessionError();
        return;
      }

      if (result.notFound) {
        console.warn(
          `Primary URL returned 404, trying secondary URL: ${getSecondaryUrl}`
        );
        result = await fetchAndCheck(getSecondaryUrl, request_id, false);

        if (!result) {
          handleFatalSessionError();
          return;
        }

        if (result?.tooManyRequests) {
          setTooManyAttempts(true);
          setShowHighTrafficMessage(false);
          setHighTrafficTimeoutExceeded(false);
          setEdgeRetryCount(0);
          setLoadingAnimationComplete(true);
          edgeRetryStartRef.current = null;
          return;
        }

        if (result?.already_done) {
          setShowHighTrafficMessage(false);
          setHighTrafficTimeoutExceeded(false);
          setEdgeRetryCount(0);
          setLoadingAnimationComplete(true);
          setShowEnglishLoadingDelayed(true);
          dispatch({ type: "SET_ALREADY_DONE", payload: true });
          setErrorGettingData(false);
          setRetryCount(0);
          edgeRetryStartRef.current = null;
          return;
        }

        if (result?.otherError || result.notFound) {
          handleFatalSessionError();
          return;
        }
      }

      if (result) {
        const wasHighTraffic = showHighTrafficMessage;
        if (showHighTrafficMessage) {
          setShowHighTrafficMessage(false);
        }
        if (edgeRetryCount !== 0) {
          setEdgeRetryCount(0);
        }
        if (highTrafficTimeoutExceeded) {
          setHighTrafficTimeoutExceeded(false);
        }
        if (tooManyAttempts) {
          setTooManyAttempts(false);
        }
        edgeRetryStartRef.current = null;

        if (
          shouldShowEnglishLoading &&
          (wasHighTraffic || !showEnglishLoadingDelayed)
        ) {
          setLoadingAnimationComplete(true);
        }

        const isReactNativeFlag =
          result.data?.isReactNative ??
          result.data?.settings?.isReactNative ??
          false;
        dispatch({
          type: "SET_DATA",
          payload: { ...result.data, isReactNative: isReactNativeFlag },
        });
        dispatch({
          type: "SET_ALREADY_DONE",
          payload: nextPath ? false : result.already_done,
        });

        dispatch({ type: "SET_SETTINGS", payload: result.data.settings });
        dispatch({ type: "SET_TEXT", payload: result.data.settings.text });

        handleRouteSequence(dispatch, result, nextPath);
        setErrorGettingData(false);
        setRetryCount(0);
      } else {
        console.error("Failed to fetch data from both primary and secondary URLs");
        edgeRetryStartRef.current = null;
        setErrorGettingData(true);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      edgeRetryStartRef.current = null;
      setErrorGettingData(true);
    }
  };

  const handleRetry = () => {
    if (retryCount < 1) {
      setRetryCount((prevCount) => prevCount + 1);
      edgeRetryStartRef.current = null;
      setEdgeRetryCount(0);
      setShowHighTrafficMessage(false);
      setHighTrafficTimeoutExceeded(false);
      setTooManyAttempts(false);
      setShowEnglishLoadingDelayed(false);
      setLoadingAnimationComplete(!shouldShowEnglishLoading);
      setErrorGettingData(false);
      fetchData(request_id, dispatch, nextPath);
    }
  };

  // Handle initialSessionData from root.js if provided
  useEffect(() => {
    if (initialSessionData && !state.data) {

      const isReactNativeFlag =
        initialSessionData.data?.isReactNative ??
        initialSessionData.data?.settings?.isReactNative ??
        false;

      dispatch({
        type: "SET_DATA",
        payload: { ...initialSessionData.data, isReactNative: isReactNativeFlag },
      });
      dispatch({
        type: "SET_ALREADY_DONE",
        payload: initialSessionData.already_done || false,
      });
      dispatch({ type: "SET_SETTINGS", payload: initialSessionData.data.settings });
      dispatch({ type: "SET_TEXT", payload: initialSessionData.data.settings.text });
      handleRouteSequence(dispatch, initialSessionData, null);

      // Skip English loader if data arrived from root.js (already fetched)
      // This prevents infinite loading when using initialSessionData
      if (shouldShowEnglishLoading && !loadingAnimationComplete) {
        setLoadingAnimationComplete(true);
      }
    }
  }, [initialSessionData]);

  // Fetch session data if not available
useEffect(() => {
  // Skip if we already have data OR if initialSessionData is provided (will be handled by other useEffect)
  if (state.data || initialSessionData) {
    // We already have the data from initialization, just handle nextPath adjustments if needed
    if (nextPath && state.routeSequence.length > 0) {
      let updatedRouteSequence = [...state.routeSequence];
      
      const nextPathIndex = updatedRouteSequence.findIndex(
        (route) => route.path === nextPath
      );

      if (nextPathIndex > -1) {
        updatedRouteSequence = updatedRouteSequence.filter(
          (_, index) => index >= nextPathIndex
        );
      } else {
        const lastRoute =
          updatedRouteSequence[updatedRouteSequence.length - 1];
        const newRoute = {
          ...lastRoute,
          path: nextPath,
          shouldProceedKey: nextPath,
        };

        updatedRouteSequence.unshift(newRoute);
      }

      if (nextPath === "dcams/mitek") {
        updatedRouteSequence = [updatedRouteSequence[0]];
      }
      
      dispatch({ type: "SET_ROUTE_SEQUENCE", payload: updatedRouteSequence });
    }

    setErrorGettingData(false);
    return; // Don't continue with the fetch if we already have data
  }

  // Fetch session data
  const doFetch = async () => {
    if (highTrafficTimeoutExceeded || tooManyAttempts) {
      return;
    }
    let localRequestId = request_id

    // Case A: if request_id is clear / oneid / digilocker, set localRequestId to the 'state' query param
    if (
      localRequestId === 'clear' ||
      localRequestId === 'clear/return' ||
      localRequestId === 'oneid' ||
      localRequestId === 'oneid/return' ||
      localRequestId === 'digilocker' ||
      localRequestId === 'digilocker/return/success' ||
      localRequestId === 'digilocker/return/failure'
    ) {
      localRequestId = searchParams.get('state')
      // Remove "-edge" suffix if present for the actual request_id
      if (localRequestId?.endsWith('-edge')) {
        localRequestId = localRequestId.replace('-edge', '')
      }
    } else if (localRequestId?.endsWith('-edge')) {
      // If request_id itself ends with "-edge", remove it
      localRequestId = localRequestId.replace('-edge', '')
    }
    // Case B: if request_id is "mdl", fetch the real request_id using the session_id query param
    else if (localRequestId === 'mdl') {
      const session_id = searchParams.get('sessionId')
      if (!session_id) {
        console.error('No session_id in query params.')
        return
      }
      try {
        localRequestId = await getRequestIdFromSession(session_id)
      } catch (err) {
        console.error('Could not exchange session for token:', err)
        setErrorGettingData(true)
        return
      }
    }
    // Case B2: if request_id is "trinsic" or a trinsic provider return path, fetch real request_id using sessionId
    else if (
      localRequestId === 'trinsic' ||
      localRequestId === 'trinsic/return' ||
      localRequestId === 'bankid-cz/return' ||
      localRequestId === 'finnish-trust-network/return' ||
      localRequestId === 'mojeid/return'
    ) {
      const session_id = searchParams.get('sessionId')
      if (!session_id) {
        console.error('No sessionId in query params for Trinsic return.')
        return
      }
      try {
        localRequestId = await getRequestIdFromSession(session_id)
        // If request_id is null, we're in edge mode - try to get from sessionStorage
        if (localRequestId === null || localRequestId === undefined) {
          const storedRequestId = sessionStorage.getItem('edge_request_id')
          if (storedRequestId) {
            console.log('Edge mode detected for Trinsic return, using stored request_id:', storedRequestId)
            localRequestId = storedRequestId
          } else {
            console.error('Edge mode Trinsic return but no stored request_id found')
            setErrorGettingData(true)
            return
          }
        }
      } catch (err) {
        console.error('Could not exchange session for token (Trinsic):', err)
        setErrorGettingData(true)
        return
      }
    }

    // Case C: if we have a valid localRequestId and it's not "upload", fetch the data
    if (localRequestId && localRequestId !== 'upload') {
      try {
        await fetchData(localRequestId, dispatch, nextPath)
        setErrorGettingData(false)
      } catch {
        setErrorGettingData(true)
      }
    }
  }

  // Only fetch if we don't have data yet
  if (!state.data) {
    doFetch()
  }
}, [request_id, dispatch, nextPath, state.data, highTrafficTimeoutExceeded, tooManyAttempts])

  useEffect(() => {
    if (qr && state.settings) {
      const requestOptions = {
        method: "POST",
        body: JSON.stringify({
          request_id: state.settings.request_id,
          type: "set",
          site_name: state.settings.site_name,
        }),
      };

      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/qrStatus`,
        requestOptions
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          //console.log("QR status update successful:", data);
        })
        .catch((error) => {
          console.error("Error updating QR status:", error);
        });
    } else {
      if (qr) {
        //console.log("QR Code detected but settings are not available yet.");
      }
    }
  }, [qr, state.settings]);

  const navigateToNext = () => {
    // Check if it's the right time to navigate to the next route
    // Allow navigation when triggerNavigation is true OR status is PENDING/PASS
    if (state.isCurrentComponentComplete && (state.triggerNavigation || state.status === "PENDING" || state.status === "PASS")) {
      dispatch({ type: "INCREMENT_COMPLETED_ROUTES" });
      const currentRoute = state.routeSequence[state.currentIndex];
      const nextIndex = state.currentIndex + 1;
      const nextRoute = state.routeSequence[nextIndex];
      if (state.settings.journey.settings?.isModular) {
        navigate("agematch/modular");
      }
      if (nextRoute) {
        dispatch({ type: "SET_CURRENT_INDEX", payload: nextIndex });
        navigate(nextRoute.path);
      } else {
        //window.top.postMessage("FAIL", "*");
        // Handle the end of the sequence or define a default action if required
      }
    }
  };

  useEffect(() => {
    let event = null;
    let next = state.triggerNavigation ? true : false;
    let error = "NONE";

    if (next) {
      event = "NEXT";
    } else if (state.closedClicked) {
      event = "CLOSED";
    } else if (state.alreadyDone) {
      event = "ERROR";
      error = "ALREADY DONE";
    } else {
      switch (state.status) {
        case "PASS":
        case "FAIL":
          event = "COMPLETE";
          break;
        default:
          event = null;
      }
    }

    // Send payload message (function handles site_name filtering internally)
    if (event) {
      postPayloadMessage({
        event: event,
        app: state.routeSequence[state.currentIndex]?.app,
        error: error,
        status: state.status,
      });
    }

    // Send PASS/FAIL message when status is terminal
    // Allow sending if: not navigating OR on last route OR on handoff
    const currentRoute = state.routeSequence?.[state.currentIndex];
    const isLastRoute = state.currentIndex >= state.routeSequence.length - 1;
    const isHandoff = currentRoute?.path === "handoff" || currentRoute?.path?.includes("handoff");
    const shouldSendTerminalStatus = !state.triggerNavigation || isLastRoute || isHandoff;

    if ((state.status === "PASS" || state.status === "FAIL") && shouldSendTerminalStatus) {
      // Check if redirect_url exists in data - if so, redirect instead of postMessage
      const redirectUrl = state.data?.redirect_url;
      if (redirectUrl) {
        const requestId = state.settings?.request_id || request_id;
        const url = new URL(redirectUrl);
        url.searchParams.append("request_id", requestId);
        window.top.location.href = url.toString();
      } else {
        postMessage(state.status);
      }
    }

    if (state.alreadyDone) {
      postMessage("ALREADY DONE");
    }
  }, [state.status, state.triggerNavigation, state.closedClicked, state.alreadyDone]);

  useEffect(() => {
    //console.log("route sequence", state.routeSequence);
    if (!state.triggerNavigation) {
      return;
    }
    navigateToNext();
  }, [state.triggerNavigation, state.routeSequence]); // Only re-run if these specific parts of the state change

  // Context value that will be provided to the consuming components
  const contextValue = {
    ...state, // Spread the state
    dispatch, // Allow dispatch to be used by consuming components
    navigateToNext, // Add other functions and values here
    postMessage,
    postPayloadMessage,
  };


  //const inIframe = window.self !== window.top;

  // Only render children when:
  // 1. We have data from the backend
  // 2. User is not a bot or blocked (OR test mode is active)
  // 3. We have fingerprint data (or fingerprinting is disabled)
  // 4. We have completed the fingerprint check (fingerprintSent is true)
  // 5. Loading animation has completed (if English loading was shown)
  const dataReady =
    state.data &&
    ((!isBot && !isBlocked) || testModeActive) &&
    (fpjs || internalFingerprintDisabled) &&
    fingerprintSent;

  const shouldRenderChildren = dataReady && loadingAnimationComplete;

  // Handle loading animation completion
  const handleLoadingComplete = () => {
    setLoadingAnimationComplete(true);
  };

  // Delay showing English loading to prevent restart when data arrives quickly
  // Once we start showing it, keep showing it until loading is complete
  useEffect(() => {
    if (shouldShowEnglishLoading && !showEnglishLoadingDelayed && !loadingAnimationComplete) {
      const timer = setTimeout(() => {
        setShowEnglishLoadingDelayed(true);
      }, 500); // 500ms delay to prevent flicker

      return () => clearTimeout(timer);
    }
  }, [shouldShowEnglishLoading, showEnglishLoadingDelayed, loadingAnimationComplete]);

  // Determine what content to render (but don't return yet)
  let content;
  let contentType = 'unknown';
  const highTrafficNotice = (
    <Box
      sx={(theme) => ({
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor:
          theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
      })}
    >
      <Center sx={{ flex: 1, padding: "md" }}>
        <Stack align="center" spacing="md">
          <Loader size="md" />
          <Text ta="center" fz="lg" fw={600}>
            We're experiencing higher than normal traffic
          </Text>
          <Text ta="center" fz="sm" c="dimmed">
            Please be patient while we prepare your session. This may take a few minutes.
          </Text>
        </Stack>
      </Center>
    </Box>
  );
  
  // Check for error states first
  if (tooManyAttempts) {
    contentType = 'error-too-many-attempts';
    content = (
      <ErrorMessage
        title="Too Many Attempts"
        subtitle={
          <Text ta="center">
            You've tried to start this verification too many times. Please try again later.
          </Text>
        }
      />
    );
  } else if (highTrafficTimeoutExceeded) {
    contentType = 'error-high-traffic-timeout';
    content = (
      <ErrorMessage
        title="Please try again later"
        subtitle={
          <Text ta="center">
            We're experiencing higher than normal traffic. Please try again in a few minutes.
          </Text>
        }
      />
    );
  } else if (state.alreadyDone) {
    contentType = 'error-expired';
    content = (
      <ErrorMessage
        title="Link Expired"
        subtitle={<Text>This link is either invalid, expired, or has already been used</Text>}
      />
    );
  } else if (errorGettingData) {
    contentType = 'error-fetch-session';
    content = (
      <ErrorMessage
        title="We're having trouble"
        subtitle={
          <>
            <Text ta="center">We couldn't retrieve your session. Please try again.</Text>
            {retryCount <= 0 && (
              <Button
                mt="lg"
                fullWidth
                onClick={handleRetry}
                disabled={retryCount >= 1}
              >
                Try Again
              </Button>
            )}
          </>
        }
      />
    );
  } else if (overload) {
    contentType = 'overload';
    content = (
      <ErrorMessage
        title="Please Wait"
        subtitle={
          <Group>
            <Loader size={"xs"} />
            <Text>We are getting ready for your verification</Text>
          </Group>
        }
      />
    );
  } else if ((isBot || isBlocked) && !testModeActive) {
    contentType = 'bot-blocked';
    content = (
      <ErrorMessage
        title="You May Not Continue"
        subtitle={<Text>You are unable to continue this process</Text>}
      />
    );
  } else if (state.status !== "PENDING" && (() => {
    // Show result screen when status is PASS/FAIL
    // Allow showing if: not navigating OR on last route OR on handoff
    const resultCurrentRoute = state.routeSequence?.[state.currentIndex];
    const resultIsLastRoute = state.currentIndex >= state.routeSequence.length - 1;
    const resultIsHandoff = resultCurrentRoute?.path === "handoff" || resultCurrentRoute?.path?.includes("handoff");
    return !state.triggerNavigation || resultIsLastRoute || resultIsHandoff;
  })()) {
    contentType = 'status-result';
    // Handle PASS/FAIL status
    const messages = state.data?.resultMessages ?? {};
    const successTitle =
      messages.success?.verificationSuccessTitle ?? "Verification Success";
    const successSubTitle =
      messages.success?.verificationSuccessSubTitle ??
      "Please return to the other tab";
    const failureTitle =
      messages.failure?.verificationFailureTitle ?? "Verification Failure";
    const failureSubTitle =
      messages.failure?.verificationFailureSubTitle ??
      "Please return to the other tab";

    // Create theme configuration for the overlay
    const colors_array = state.data?.styles?.colors
      ? state.data.styles.colors
      : {
          "vx-green": [
            "#eff6fb",
            "#dee9f1",
            "#b8d2e5",
            "#8fbad8",
            "#6ea5ce",
            "#5998c8",
            "#4d92c6",
            "#3e7eaf",
            "#34709d",
            "#22618b",
          ],
        };

    const themeConfig = {
      colors: colors_array,
      colorScheme: state.data?.styles?.colorScheme
        ? state.data.styles.colorScheme
        : "light",
      primaryColor: state.data?.styles?.primaryColor
        ? state.data.styles.primaryColor
        : "vx-green",
      primaryShade: state.data?.styles?.primaryShade ? state.data.styles.primaryShade : 5,
      components: {
        Button: {
          defaultProps: {
            radius: state.data?.styles?.button?.radius
              ? state.data.styles.button.radius
              : 6,
          },
        },
      },
      defaultRadius: state.data?.styles?.defaultRadius ? state.data.defaultRadius : 6,
    };

    content = (
      <MantineProvider theme={themeConfig} withGlobalStyles withNormalizeCSS>
        <Navigation logo_url={""} />
        <CenterLoadingOverlay
          content={
            <Center>
              <Stack align="center">
                <ThemeIcon
                  variant="light"
                  color={state.status === "PASS" ? "green" : "red"}
                  size={95}
                  radius="xl"
                >
                  {state.status === "PASS" ? (
                    <IconShieldCheck size={85} stroke={1.5} />
                  ) : (
                    <IconExclamationCircle size={85} stroke={1.5} />
                  )}
                </ThemeIcon>
                <Text align="center" fz="xl" fw={700}>
                  {state.status === "PASS" ? successTitle : failureTitle}
                </Text>
                <Text align="center" mt="md" fz="md">
                  {state.status === "PASS" ? successSubTitle : failureSubTitle}
                </Text>
              </Stack>
            </Center>
          }
        />
      </MantineProvider>
    );
  } else if (shouldRenderChildren) {
    contentType = 'children';
    // Everything is ready, render children
    content = children;
  } else {
    // Still loading - show appropriate loading component
    // Once English loading has started showing, keep showing it until complete
    if (showEnglishLoadingDelayed && !loadingAnimationComplete) {
      if (showHighTrafficMessage) {
        contentType = 'english-loading-high-traffic';
        content = highTrafficNotice;
      } else {
        contentType = 'english-loading';
        // Default loading messages for the animated loader
        const loadingMessages = [
          "Getting your session ready...",
          "Next, you'll confirm your age and identity...",
          "Your data is encrypted and never shared...",
          "You're about to join millions of other verified users...",
          "Finalizing..."
        ];

        content = (
          <EnglishLoadingWithCompletion
            key="english-loading" // Key ensures it doesn't remount
            onComplete={handleLoadingComplete}
            messages={loadingMessages}
          />
        );
      }
    } else if (shouldShowEnglishLoading && !showEnglishLoadingDelayed) {
      // Show interim loading before animated loader kicks in
      contentType = showHighTrafficMessage
        ? 'simple-loading-pre-english-high-traffic'
        : 'simple-loading-pre-english';
      content = showHighTrafficMessage ? highTrafficNotice : <LoadingOverlay visible />;
    } else {
      // Show simple loading overlay for non-English browsers
      contentType = showHighTrafficMessage
        ? 'simple-loading-high-traffic'
        : 'simple-loading';
      content = showHighTrafficMessage ? highTrafficNotice : <LoadingOverlay visible />;
    }
  }

  const contentOverride = contentType === 'children' ? null : content;

  const extendedContextValue = {
    ...contextValue,
    contentOverride,
    contentType,
  };

  // Check if we should show test mode banner
  const shouldShowTestModeBanner = (isBot || isBlocked) && testModeActive && !isTestModeBannerDismissed;

  // Single return at the end after all hooks
  return (
    <DataContext.Provider value={extendedContextValue}>
      {/*<Navigation logo_url={state.data?.logo_url ? state.data.logo_url : ""} />*/}
      {shouldShowTestModeBanner && (
        <Alert
          color="orange"
          withCloseButton
          onClose={() => setIsTestModeBannerDismissed(true)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            borderRadius: 0,
            padding: "8px",
            minHeight: "auto"
          }}
        >
          <Text align="center" fw={600} size="sm">
            TEST MODE: This session would have been blocked but you're in test mode
          </Text>
        </Alert>
      )}
      {contentOverride || children}
    </DataContext.Provider>
  );
};
